import {
  addCpfMask,
  ButtonLink,
  convertEventSelectToTarget,
  formatDataToLabelValue,
  FormFeedBack,
  MainButton,
  removeCpfMask,
  Select,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import { CpfInput } from 'adasi_components';
import {
  AddPersonToEntityUseCaseInterface,
  CourseListUseCaseInterface,
  GetAllPersonsFromEntityUseCaseInterface,
  GetAllProfessionsUseCaseInterface,
} from 'domain/usecases';
import { SEX } from 'domain/value-objects';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { sexOptions } from 'presentation/app/modules/Entities/models/sex-options';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { clearAddPersonToEntityModalData } from 'presentation/config/store/entities/entities-slice';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import * as Yup from 'yup';

interface IProps {
  isOpen: boolean;
  entityId: string;
  searchedCPFRef: MutableRefObject<string>;
  profileType: PROFILE_TYPE;
  toggle: () => void;
  requestGetAllPersonsFromEntity: (
    params: GetAllPersonsFromEntityUseCaseInterface.Params,
  ) => void;
}

export const AddPersonToEntityModal = ({
  isOpen,
  entityId,
  searchedCPFRef,
  profileType,
  toggle,
  requestGetAllPersonsFromEntity,
}: IProps) => {
  // Hooks
  const { entitiesService, courseService, professionsService } = useService();
  const { addPersonToEntityModalData } = useAppSelector(
    (state) => state.Entities,
  );
  const dispatch = useDispatch();

  // States
  const [showPassword, setShowPassword] = useState(false);
  const [coursesSelectOptions, setCoursesSelectOptions] = useState<
    CourseListUseCaseInterface.Result[]
  >([]);
  const [professionsSelectOptions, setProfessionsSelectOptions] =
    useState<GetAllProfessionsUseCaseInterface.Result>(null);
  const [requestGetAllCoursesStatus, setRequestGetAllCoursesStatus] = useState(
    STATUSPAGE.DONE,
  );
  const [requestGetAllProfessionsStatus, setRequestGetAllProfessionsStatus] =
    useState(STATUSPAGE.DONE);
  const [requestAddPersonToEntityStatus, setRequestAddPersonToEntityStatus] =
    useState(STATUSPAGE.DONE);

  // Methods
  const toggleShowPassword = useCallback(() => {
    setShowPassword((prevState) => !prevState);
  }, []);

  const removeAddPersonToEntityModalData = useCallback(() => {
    dispatch(clearAddPersonToEntityModalData());
    searchedCPFRef.current = '';
  }, []);

  const requestGetAllCoursesFromEntity = useCallback(
    async (params: CourseListUseCaseInterface.Params) => {
      setRequestGetAllCoursesStatus(STATUSPAGE.LOADING);
      await courseService
        .courseList(params)
        .then((res) => setCoursesSelectOptions(res))
        .catch(() => {})
        .finally(() => {
          setRequestGetAllCoursesStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllProfessions = useCallback(
    async (params?: GetAllProfessionsUseCaseInterface.Params) => {
      setRequestGetAllProfessionsStatus(STATUSPAGE.LOADING);
      await professionsService
        .getAllProfessions(params)
        .then((res) => setProfessionsSelectOptions(res))
        .catch(() => {})
        .finally(() => {
          setRequestGetAllProfessionsStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestAddPersonToEntity = useCallback(
    async (params: AddPersonToEntityUseCaseInterface.Params) => {
      setRequestAddPersonToEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .addPersonToEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          toggle();
          requestGetAllPersonsFromEntity({ entityId, profileType });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestAddPersonToEntityStatus(STATUSPAGE.DONE);
        });
    },
    [entityId, profileType],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cpf: addCpfMask(searchedCPFRef.current),
      name: addPersonToEntityModalData?.name || '',
      sex: addPersonToEntityModalData?.sex
        ? addPersonToEntityModalData.sex === SEX.MALE
          ? sexOptions[0]
          : sexOptions[1]
        : null,
      email: addPersonToEntityModalData?.email || '',
      profession: addPersonToEntityModalData?.profession
        ? {
            value: addPersonToEntityModalData.profession.id,
            label: addPersonToEntityModalData.profession.name,
          }
        : null,
      course: null,
      password: '',
    },
    validationSchema: Yup.object({
      cpf: Yup.string().cpfType('CPF inválido').required('Campo é obrigatório'),
      name: Yup.string().required('Campo é obrigatório'),
      sex: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
      email: Yup.string().email('Email inválido').notRequired(),
      profession: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.number(),
        })
        .notRequired(),
      course:
        profileType !== PROFILE_TYPE.DIRECTOR
          ? Yup.object()
              .shape({
                label: Yup.string(),
                value: Yup.string(),
              })
              .required('Campo é obrigatório')
          : Yup.object()
              .shape({
                label: Yup.string(),
                value: Yup.string(),
              })
              .notRequired(),
      password: addPersonToEntityModalData
        ? Yup.string()
            .notRequired()
            .min(8, 'A senha deve conter no mínimo 8 caracteres')
            .max(32, 'A senha deve conter no máximo 32 caracteres')
            .matches(
              /^(?=.*[a-z])/,
              'A senha deve conter pelo menos uma letra minúscula',
            )
            .matches(
              /^(?=.*[A-Z])/,
              'A senha deve conter pelo menos uma letra maiúscula',
            )
            .matches(/^(?=.*\d)/, 'A senha deve conter pelo menos um número')
            .matches(
              /^(?=.*[@#$%&*!])/,
              'A senha deve conter pelo menos um caractere especial (@, #, $, %, &, *, !)',
            )
        : Yup.string()
            .required('Campo é obrigatório')
            .min(8, 'A senha deve conter no mínimo 8 caracteres')
            .matches(
              /^(?=.*[a-z])/,
              'A senha deve conter pelo menos uma letra minúscula',
            )
            .matches(
              /^(?=.*[A-Z])/,
              'A senha deve conter pelo menos uma letra maiúscula',
            )
            .matches(/^(?=.*\d)/, 'A senha deve conter pelo menos um número')
            .matches(
              /^(?=.*[@#$%&*!])/,
              'A senha deve conter pelo menos um caractere especial (@, #, $, %, &, *, !)',
            ),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        personId: addPersonToEntityModalData?.id,
        entityId,
        profileType,
        cpf: removeCpfMask(values.cpf),
        name: values.name,
        sex: values.sex?.value as SEX,
        email: values.email,
        professionId: values.profession?.value,
        ...(profileType !== PROFILE_TYPE.DIRECTOR
          ? { courseId: values.course.value }
          : {}),
        password: values.password || undefined,
      };
      requestAddPersonToEntity(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllCoursesFromEntity({ entityId });
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      id="addPersonToEntityModal"
      backdrop={'static'}
      onClosed={() => {
        removeAddPersonToEntityModalData();
        validation.resetForm();
      }}
    >
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalHeader toggle={toggle}>Adicionar Membro</ModalHeader>

        <ModalBody>
          <Row>
            <Col xs={12} className="mb-3">
              <Label>CPF</Label>
              <CpfInput
                name="cpf"
                handleChange={validation.handleChange}
                inputId="cpf"
                placeholder="Informe o CPF"
                value={validation.values.cpf}
                className="form-control"
                disabled
                invalid={!!(validation.touched.cpf && validation.errors.cpf)}
              />
              <FormFeedBack
                error={String(validation.errors?.cpf)}
                isInvalid={
                  !!(validation.touched?.cpf && validation.errors?.cpf)
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                type="text"
                name="name"
                onBlur={validation.handleBlur}
                value={validation?.values?.name}
                onChange={validation.handleChange}
                disabled={requestAddPersonToEntityStatus === STATUSPAGE.LOADING}
                invalid={
                  validation?.touched?.name && !!validation?.errors?.name
                }
              />
              <FormFeedBack
                error={validation?.errors?.name}
                isInvalid={
                  validation?.touched?.name && !!validation?.errors?.name
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="sex">Sexo</Label>
              <Select
                isClearable
                id="sex"
                name="sex"
                placeholder="Selecione"
                onBlur={validation.handleBlur}
                value={validation?.values?.sex}
                options={sexOptions}
                onChange={(e) => {
                  validation.handleChange(convertEventSelectToTarget('sex', e));
                }}
                isDisabled={
                  requestAddPersonToEntityStatus === STATUSPAGE.LOADING
                }
                invalid={
                  !!(validation?.touched?.sex && validation?.errors?.sex)
                }
              />
              <FormFeedBack
                error={String(validation.errors?.sex)}
                isInvalid={
                  !!(validation.touched?.sex && validation.errors?.sex)
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="email">E-mail</Label>
              <Input
                name="email"
                id="email"
                type="email"
                className="form-control"
                onBlur={validation.handleBlur}
                value={validation.values.email}
                onChange={validation.handleChange}
                disabled={requestAddPersonToEntityStatus === STATUSPAGE.LOADING}
                invalid={
                  !!(validation.touched.email && validation.errors?.email)
                }
              />
              <FormFeedBack
                error={validation?.errors?.email}
                isInvalid={
                  validation?.touched?.email && !!validation?.errors?.email
                }
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Label htmlFor="profession">Profissão</Label>
              <SelectRemote
                name="profession"
                isClearable
                clearOptions={() => setProfessionsSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setProfessionsSelectOptions(null);
                    return;
                  }
                  if (searchString.length < 3) return;

                  requestGetAllProfessions({ query: searchString });
                }}
                inputId="profession"
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('profession', e),
                  );
                }}
                onBlur={() => {
                  setProfessionsSelectOptions(null);
                }}
                options={professionsSelectOptions?.map((professionOption) => {
                  return {
                    value: professionOption.id,
                    label: professionOption.name,
                  };
                })}
                value={validation?.values?.profession}
                placeholder="Selecione uma Profissão"
                isDisabled={
                  requestAddPersonToEntityStatus === STATUSPAGE.LOADING
                }
                loading={requestGetAllProfessionsStatus === STATUSPAGE.LOADING}
              />
              <FormFeedBack
                error={String(validation.errors?.profession)}
                isInvalid={
                  !!(
                    validation.touched?.profession &&
                    validation.errors?.profession
                  )
                }
              />
            </Col>

            {profileType !== PROFILE_TYPE.DIRECTOR && (
              <Col xs={12} className="mb-3">
                <Label htmlFor="course">Curso</Label>
                <Select
                  isClearable
                  id="course"
                  name="course"
                  placeholder="Selecione um Curso"
                  onBlur={validation.handleBlur}
                  value={validation?.values?.course}
                  options={formatDataToLabelValue(
                    coursesSelectOptions as never,
                    'id',
                    'nome',
                  )}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('course', e),
                    );
                  }}
                  isDisabled={
                    requestGetAllCoursesStatus === STATUSPAGE.LOADING ||
                    requestAddPersonToEntityStatus === STATUSPAGE.LOADING
                  }
                  isLoading={requestGetAllCoursesStatus === STATUSPAGE.LOADING}
                  invalid={
                    !!(
                      validation?.touched?.course && validation?.errors?.course
                    )
                  }
                />
                <FormFeedBack
                  error={String(validation.errors?.course)}
                  isInvalid={
                    !!(validation.touched?.course && validation.errors?.course)
                  }
                />
              </Col>
            )}
            {!addPersonToEntityModalData && (
              <Col xs={12} className="mb-3">
                <Label htmlFor="password">Definir Senha</Label>
                <div className="position-relative">
                  <Input
                    name="password"
                    id="password"
                    value={validation.values.password}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha"
                    maxLength={32}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    disabled={
                      requestAddPersonToEntityStatus === STATUSPAGE.LOADING
                    }
                    invalid={
                      !!(
                        validation.touched?.password &&
                        validation.errors?.password
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.password}
                    isInvalid={
                      validation?.touched?.password &&
                      !!validation?.errors?.password
                    }
                  />
                  <button
                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted me-3"
                    type="button"
                    id="password-addon"
                    disabled={
                      requestAddPersonToEntityStatus === STATUSPAGE.LOADING
                    }
                    onClick={() => toggleShowPassword()}
                  >
                    <i
                      className={`ri-eye-${showPassword ? 'off-' : ''}fill align-middle`}
                    ></i>
                  </button>
                </div>
              </Col>
            )}
          </Row>
        </ModalBody>

        <ModalFooter>
          <ButtonLink
            type="button"
            onClick={toggle}
            disabled={requestAddPersonToEntityStatus === STATUSPAGE.LOADING}
          >
            Cancelar
          </ButtonLink>
          <MainButton
            type="submit"
            isDisabled={requestAddPersonToEntityStatus === STATUSPAGE.LOADING}
            isLoading={requestAddPersonToEntityStatus === STATUSPAGE.LOADING}
          >
            Salvar
          </MainButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
